import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, GitMerge, Undo2 } from "lucide-react";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { Button } from "@/components/ui/button";
import { Loading, EmptyState } from "@/components/common/Loading";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Duplicate suggestions + previewed, undoable merge. A suggestion never mutates data. */
export default function Duplicates() {
  const { visible, loading } = useCrmConfig();
  const [pairs, setPairs] = useState(null);
  const [history, setHistory] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [d, h] = await Promise.all([api.get("/crm/duplicates"), api.get("/crm/merge/history")]);
    setPairs(d.data.pairs || []);
    setHistory(h.data.merges || []);
  }, []);

  useEffect(() => { if (visible) load().catch(() => setPairs([])); }, [visible, load]);

  const openPlan = async (primary_id, duplicate_id) => {
    try {
      const { data } = await api.post("/crm/merge/preview", { primary_id, duplicate_id });
      setPlan(data);
    } catch { toast.error("Couldn't preview that merge"); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      await api.post("/crm/merge/commit", { primary_id: plan.primary.id,
        duplicate_id: plan.duplicate.id });
      toast.success("Merged — you can undo this");
      setPlan(null); load();
    } catch { toast.error("Couldn't merge those contacts"); } finally { setBusy(false); }
  };

  const undo = async (id) => {
    try {
      await api.post(`/crm/merge/${id}/undo`);
      toast.success("Merge undone");
      load();
    } catch { toast.error("Couldn't undo that merge"); }
  };

  if (loading) return <Loading />;
  if (!visible) return (
    <div className="py-20 text-center" data-testid="duplicates-unavailable">
      <h1 className="text-2xl font-[680]">Page not found</h1>
    </div>
  );

  return (
    <div data-testid="duplicates-page">
      <Link to="/dashboard/contacts" data-testid="duplicates-back"
        className="inline-flex items-center gap-1.5 text-sm font-[560] text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>
      <h1 className="mt-4 text-2xl font-[680] tracking-[-0.01em]">Possible duplicates</h1>
      <p className="mt-1 text-muted-foreground">
        Nothing is merged automatically. Review a pair, see exactly what survives, then decide.
      </p>

      {!pairs ? <div className="mt-6"><Loading /></div> : pairs.length === 0 ? (
        <div className="mt-6">
          <EmptyState icon={GitMerge} testId="duplicates-empty" title="No duplicates found"
            description="We look for identical emails, phone numbers and full names in your own contacts." />
        </div>
      ) : (
        <ul className="mt-6 space-y-2" data-testid="duplicates-list">
          {pairs.map((p) => (
            <li key={p.contact_ids.join("-")} data-testid={`duplicate-${p.contact_ids[0]}`}
              className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-[600]">
                  {p.contacts[0].display_name} &nbsp;·&nbsp; {p.contacts[1].display_name}
                </span>
                <span className="block text-xs text-muted-foreground">
                  Matched on {p.reason.replace("_", " ")}
                </span>
              </span>
              <Button size="sm" variant="outline" data-testid={`duplicate-review-${p.contact_ids[0]}`}
                onClick={() => openPlan(p.contact_ids[0], p.contact_ids[1])}>
                Review merge
              </Button>
            </li>
          ))}
        </ul>
      )}

      {history.length > 0 && (
        <section className="mt-8" data-testid="merge-history">
          <h2 className="text-base font-[640]">Recent merges</h2>
          <ul className="mt-2 space-y-1.5">
            {history.map((mg) => (
              <li key={mg.id} data-testid={`merge-history-${mg.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {mg.state} · {new Date(mg.created_at).toLocaleString()}
                </span>
                {mg.state === "committed" && (
                  <Button size="sm" variant="outline" onClick={() => undo(mg.id)}
                    data-testid={`merge-undo-${mg.id}`}>
                    <Undo2 className="mr-1.5 h-4 w-4" /> Undo
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog open={!!plan} onOpenChange={(v) => !v && setPlan(null)}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge these contacts?</DialogTitle>
            <DialogDescription>
              {plan && <>“{plan.primary.display_name}” survives. “{plan.duplicate.display_name}”
                is kept recoverable so you can undo this.</>}
            </DialogDescription>
          </DialogHeader>
          {plan && (
            <div className="space-y-3 text-sm">
              <div data-testid="merge-moves">
                <span className="font-[600]">Moves across:</span>{" "}
                {Object.entries(plan.moves).filter(([, n]) => n > 0)
                  .map(([k, n]) => `${n} ${k.replace("crm_", "").replace(/_/g, " ")}`)
                  .join(", ") || "nothing extra"}
              </div>
              {plan.conflicts.length > 0 && (
                <div data-testid="merge-conflicts">
                  <span className="font-[600]">Kept as-is (recorded in the merge log):</span>
                  <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                    {plan.conflicts.map((c) => (
                      <li key={c.field}>{c.field}: keeping “{c.keep}”, not “{c.from_duplicate}”</li>
                    ))}
                  </ul>
                </div>
              )}
              {Object.keys(plan.fills).length > 0 && (
                <div data-testid="merge-fills">
                  <span className="font-[600]">Filled in from the duplicate:</span>{" "}
                  {Object.entries(plan.fills).map(([k, v]) => `${k} → ${v}`).join(", ")}
                </div>
              )}
              {plan.link.action !== "none" && (
                <div data-testid="merge-link">
                  <span className="font-[600]">Startup Otter link:</span>{" "}
                  {plan.link.action.replace("_", " ")}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlan(null)} data-testid="merge-cancel">
              Cancel
            </Button>
            <Button onClick={commit} disabled={busy} data-testid="merge-confirm">
              <GitMerge className="mr-1.5 h-4 w-4" /> Merge contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
